import * as R from 'ramda'
import { types as AuthTypes } from 'store/Auth'
import { types as NotificationTypes } from 'store/Notification'
import { clearWebStorage, isActionRelevant } from './utils'
import { datadogLogs } from '@datadog/browser-logs'

// Services that should trigger logout on 401
// These are the core Sidecar services that indicate session expiry
const AUTH_REQUIRED_SERVICES = ['auth', 'exp', 'acct', 'plcy', 'care', 'comm']

// Check if the failed request URL indicates it's from an auth-required service
const isAuthRequiredEndpoint = url => {
  if (!url || url === 'unknown') return false

  // Check if the URL contains any of our auth-required service patterns
  // URLs follow pattern: https://{env}-api.sidecarhealth.com/{service}/...
  // We need to match the service name immediately after the domain

  // Extract the path after the sidecarhealth.com domain
  // Only match actual sidecarhealth.com domain and its subdomains
  const match = url.match(/^https?:\/\/(?:[^./]+\.)*sidecarhealth\.com(\/.*)$/)
  if (!match) return false

  const path = match[1] // e.g., "/auth/v1/login" or "/exp/v2/expenses"

  // Check if the path starts with /{service}/
  return AUTH_REQUIRED_SERVICES.some(service => path.startsWith(`/${service}/`))
}

/**
 * Checks that all responses come back authorized by the backend. Should one
 * return with an "401 Unauthorized" response, we need to make sure that the
 * user is immediately logged out and info cleared.
 */
export default ({ dispatch }) =>
  next =>
  action => {
    const status = R.path(['payload', 'status'], action)

    // normally if our sidecar apis throw a 401 we want to force
    // logout the member, however if it's from a third party we should
    // handle that... eventually we can expand this to more of the actions
    // or even filter by isRSAA(action).endpoint for non sidecar apis
    const irrelevantActions = /LOOKUP_ZIP_ERROR/
    if (isActionRelevant(action, irrelevantActions)) return next(action)

    if (status === 401) {
      const url = action?.meta?.endpoint || 'unknown'

      if (isAuthRequiredEndpoint(url)) {
        // This is an auth-required service, trigger logout
        clearWebStorage()

        // dispatch notification so that user can see
        // a message on the login page.
        dispatch({
          type: NotificationTypes.SET_NOTIFICATION,
          payload: {
            type: 'warn',
            domain: 'login',
            text: 'Your session has expired, please login again.',
          },
        })

        // FORCE_LOGOUT makes sure that the entire store is reset
        // see store/reducers.js for implementation. DO NOT return
        // this as the next action because it'll block any _REQUESTS
        // that are happening and will keep things in perpetual busy
        // states. Just dispatch this and let calls play out naturally.
        dispatch({ type: AuthTypes.FORCE_LOGOUT })
      } else {
        // This is a 401 from a non-auth service, log it to Datadog
        // This helps us monitor unexpected 401s that don't trigger logout
        datadogLogs.logger.warn('401 response from non-auth service', {
          url,
        })
      }
    }

    return next(action)
  }
